/**
 * CreditScoreCard - Compact credit score card for the Home tab
 * Shows between quick navigation shortcuts and insights
 */
import { ChevronRight } from "lucide-react";
import { usePurple } from "../../utils/purple";

const CreditScoreCard = ({ creditScore = {}, theme = 'pink', onClick, tile = false, avoid = null }) => {
  const isPink = theme === 'pink';
  const isWarm = theme === 'warm';
  const purple = usePurple();

  const entries = creditScore?.entries || [];
  const goal = creditScore?.goal || 800;
  const hasEntries = entries.length > 0;

  // Get latest and previous score
  const sorted = [...entries].sort((a, b) => b.month_key.localeCompare(a.month_key));
  const latestScore = sorted[0]?.score || 0;
  const previousScore = sorted[1]?.score || null;
  const change = previousScore !== null ? latestScore - previousScore : null;

  // Score rating
  const getRating = (score) => {
    if (score >= 760) return { label: 'Excellent', color: '#047857' };
    if (score >= 725) return { label: 'Very Good', color: '#10b981' };
    if (score >= 660) return { label: 'Good', color: '#22c55e' };
    if (score >= 560) return { label: 'Fair', color: '#f59e0b' };
    return { label: 'Poor', color: '#ef4444' };
  };

  const rating = getRating(latestScore);

  // Calculate arc percentage (300-900 range)
  const scorePercent = hasEntries ? Math.min(1, Math.max(0, (latestScore - 300) / 600)) : 0;
  const fillDeg = scorePercent * 180;

  const c1 = purple ? '#D9C8F6' : isPink ? '#F7A6C2' : isWarm ? '#B07D50' : '#4361EE';
  const c2 = purple ? '#8B5CE0' : isPink ? '#C8B6F0' : isWarm ? '#7C5C3A' : '#6366f1';

  const gradientBg = fillDeg > 0
    ? `conic-gradient(from 270deg, ${c1} 0deg, ${c2} ${fillDeg}deg, transparent ${fillDeg}deg, transparent 360deg)`
    : 'transparent';
  const ringMask = 'radial-gradient(circle at center, transparent 22px, black 23px, black 31px, transparent 32px)';

  return (
    <button
      data-testid="credit-score-card"
      onClick={onClick}
      className="w-full bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 hover:shadow-md transition-shadow text-left"
    >
      {/* Mini Gauge - CSS conic-gradient */}
      <div className="flex-shrink-0 relative" style={{ width: 64, height: 40 }}>
        <div style={{ width: 64, height: 32, overflow: 'hidden', position: 'relative' }}>
          {/* Gray background ring */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            position: 'absolute', top: 0, left: 0,
            background: 'conic-gradient(from 270deg, #e5e7eb 0deg, #e5e7eb 180deg, transparent 180deg)',
            WebkitMask: ringMask, mask: ringMask,
          }} />
          {/* Colored arc */}
          {fillDeg > 0 && (
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              position: 'absolute', top: 0, left: 0,
              background: gradientBg,
              WebkitMask: ringMask, mask: ringMask,
            }} />
          )}
        </div>
        <span 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 font-bold text-gray-800"
          style={{ fontSize: hasEntries ? '16px' : '11px', fontFamily: "'DM Serif Display', serif" }}
        >
          {hasEntries ? latestScore : '---'}
        </span>
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        <h3 className="m-0" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', fontWeight: 600, color: '#1F2937' }}>Credit Score</h3>
        {hasEntries ? (
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span 
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${rating.color}20`, color: rating.color }}
            >
              {rating.label}
            </span>
            {change !== null && change !== 0 && (
              <span className="text-xs font-bold" style={{ color: change > 0 ? '#10b981' : '#ef4444' }}>
                {change > 0 ? '↑' : '↓'} {change > 0 ? '+' : ''}{change} pts
              </span>
            )}
          </div>
        ) : (
          <p className="text-[10px] text-gray-400 mt-0.5">Tap to start tracking</p>
        )}
        <p className="text-[10px] text-gray-400 mt-0.5">
          {hasEntries ? 'Tap to view history & tips' : ''}
        </p>
      </div>

      {/* Chevron */}
      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: purple ? '#F3E8FB' : isPink ? 'rgba(236,72,153,0.10)' : isWarm ? 'rgba(176,125,80,0.10)' : 'rgba(129,140,248,0.10)' }}>
          <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: purple ? '#A633C4' : isPink ? '#EC4899' : isWarm ? '#B07D50' : '#818cf8' }} />
        </div>
    </button>
  );
};

export default CreditScoreCard;
